import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fmt, usd } from '../lib/format';
import { ErrorBox, Section } from '../ui/kit';

// What would your clients cost a month? The same function the assistant uses (POST /portal/estimate, src/billing/estimate.ts):
// a plan per client plus whole credit packs, the agency discount when it is on, a total and a cost per meeting.

type Line = { label: string; usd: number };
type Option = { plan: string; name: string; lines: Line[]; per_client_usd: number; total_usd: number; packs: number; math: string; notes: string[] };
type Estimate = {
  input: { clients: number; contacts_per_month: number; mode: string }; credits_needed: number; per_contact_credits: number; emails: number;
  options: Option[]; recommended: Option; agency_discount_applied: boolean; agency_discount_note: string | null;
  meetings: [number, number] | null; cost_per_meeting_usd: [number, number] | null; notes: string[];
};

/** a number typed freely: the fallback while the field is empty, clamped when it is left */
const clamp = (v: string, lo: number, hi: number, d: number) => { const n = Math.floor(Number(v)); return Number.isFinite(n) && v.trim() !== '' ? Math.min(hi, Math.max(lo, n)) : d; };

export function AgencyCalculator() {
  const [clients, setClients] = useState('3');
  const [contacts, setContacts] = useState('500');
  const [mode, setMode] = useState<'researched' | 'volume'>('researched');
  const [est, setEst] = useState<Estimate | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const nClients = clamp(clients, 1, 200, 1);
  const nContacts = clamp(contacts, 0, 100000, 0);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api<Estimate>('/portal/estimate', { method: 'POST', body: { clients: nClients, contacts_per_month: nContacts, mode, billed_to_agency: true } })
        .then((r) => { if (live) { setEst(r); setErr(null); } }, (e) => { if (live) setErr(e); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [nClients, nContacts, mode]);
  const r = est?.recommended;
  return (
    <Section title="What would your clients cost?">
      <p className="muted small">A plan per client, plus whole credit packs when a client needs more than its plan includes. An estimate from today's prices.</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label>Clients <input type="number" min={1} max={200} value={clients} onChange={(e) => setClients(e.target.value)} onBlur={() => setClients(String(nClients))} style={{ maxWidth: 100 }} /></label>
        <label>Contacts a month, each <input type="number" min={0} max={100000} value={contacts} onChange={(e) => setContacts(e.target.value)} onBlur={() => setContacts(String(nContacts))} style={{ maxWidth: 140 }} /></label>
        <label>Research <select value={mode} onChange={(e) => setMode(e.target.value as 'researched' | 'volume')}><option value="researched">Researched</option><option value="volume">Volume (lighter check)</option></select></label>
      </div>
      {err ? <ErrorBox error={err} /> : null}
      {est && r ? (
        <div role="status" aria-live="polite">
          <p><b>{r.name}{r.packs ? ` and ${r.packs} credit pack${r.packs === 1 ? '' : 's'}` : ''} per client: {usd(r.per_client_usd)} a month each, {usd(r.total_usd)} for {fmt(est.input.clients)} client{est.input.clients === 1 ? '' : 's'}.</b></p>
          <p className="small">{r.math}</p>
          {est.options.filter((o) => o !== r).map((o) => <p key={o.plan} className="small muted">{o.name} instead: {usd(o.per_client_usd)} each, {usd(o.total_usd)} in all.</p>)}
          <p className="small muted">
            {fmt(est.input.contacts_per_month)} contacts per client = {fmt(est.credits_needed)} credits ({est.per_contact_credits} each) and {fmt(est.emails)} emails per client.
            {est.agency_discount_note ? ` ${est.agency_discount_note}.` : ''}
            {est.meetings && est.cost_per_meeting_usd ? ` About ${fmt(est.meetings[0])}–${fmt(est.meetings[1])} meetings across all clients (our estimate, not a promise), so ${usd(est.cost_per_meeting_usd[0])}–${usd(est.cost_per_meeting_usd[1])} per meeting.` : ''}
          </p>
          {est.notes.filter((n) => !/^Cancel/.test(n)).map((n) => <p key={n} className="small muted">{n}</p>)}
        </div>
      ) : null}
    </Section>
  );
}
